/**
 * Administration ids of the Dobré železiarstvo content model (api.dzl.sk, store „dzl.sk“).
 * Full map with the meaning of every field: Obsidian „Dobré železiarstvo — administrácia, id-mapa“.
 */

/** Page „Domov“ (id 8) carries the site-wide texts (page fields, `PAGE_FIELDS`). */
export const HOME_SLUG = '';

export const SECTIONS = {
  benefits: 1,
  heroSlides: 2,
  categories: 3,
  banners: 4,
  // 5 „Predajne“ is unused: „Kde nás nájdete?“ lists the pickup points (/store/place-of-deliveries).
  popularSearches: 6,
  searchSuggestions: 7,
  brands: 8,
  faq: 9,
  seoCategories: 10,
  payments: 11,
  cities: 12,
  dowelAdvisor: 13,
  paintAdvisor: 14,
} as const;

export const FIELDS = {
  benefits: { icon: 1, text: 2, showOn: 3 },
  heroSlides: {
    label: 4,
    title: 5,
    text: 6,
    button1Label: 7,
    button1Href: 8,
    button2Label: 9,
    button2Href: 10,
    button2Icon: 11,
    image: 12,
    imageMobile: 13,
  },
  categories: {
    icon: 14,
    href: 15,
    showOn: 16,
    tipLabel: 17,
    tipTitle: 18,
    tipText: 19,
    tipButton: 20,
    tipHref: 21,
    tipImage: 22,
  },
  banners: { label: 23, title: 24, text: 25, button: 26, href: 27, image: 28, style: 29 },
  popularSearches: { href: 36 },
  searchSuggestions: { href: 37 },
  brands: { logo: 38, href: 39 },
  faq: { answer: 40, showOn: 41 },
  seoCategories: { icon: 42, text: 43, links: 44, href: 45 },
  payments: { logo: 46 },
  cities: { icon: 47, text: 48, href: 49 },
  dowelAdvisor: {
    material: 50,
    load: 51,
    object: 52,
    product: 53,
    description: 54,
    diameter: 55,
    screw: 56,
    suitableFor: 57,
  },
  paintAdvisor: { product: 58, packLitres: 59, coverage: 60 },
} as const;

/** Values of the multiselect fields. */
export const VALUES = {
  benefits: { home: 1, product: 10 },
  categories: { home: 2, catalog: 3, search: 4 },
  banners: { dark: 5, red: 6 },
  faq: { home: 9 },
  dowelAdvisor: {
    material: {
      concrete: 11,
      solidBrick: 12,
      hollowBrick: 13,
      aeratedConcrete: 14,
      plasterboard: 15,
    },
    load: { light: 16, medium: 17, heavy: 18 },
    object: { picture: 19, shelf: 20, kitchenCabinet: 21, tvMount: 22, curtainRod: 23 },
  },
} as const;

/** Page fields of „Domov“ by name (`pageText(page, PAGE_FIELDS.…)`). */
export const PAGE_FIELDS = {
  topbarText: 'Topbar - text',
  phoneLabel: 'Kontakt - telefón popis',
  emailLabel: 'Kontakt - e-mail popis',
  adviceLabel: 'Poradenstvo - štítok',
  adviceTitle: 'Poradenstvo - nadpis',
  adviceText: 'Poradenstvo - text',
  categoriesTitle: 'Kategórie - nadpis',
  categoriesAll: 'Kategórie - všetky',
  categoriesAllHref: 'Kategórie - všetky odkaz',
  categoriesAllIcon: 'Kategórie - všetky ikona',
  productsTitle: 'Obľúbené produkty - nadpis',
  productsBestsellers: 'Obľúbené produkty - najpredávanejšie',
  productsNew: 'Obľúbené produkty - novinky',
  productsRecommended: 'Obľúbené produkty - odporúčame',
  productsTopRated: 'Obľúbené produkty - najlepšie hodnotené',
  dealsTitle: 'Akciové ponuky - nadpis',
  dealsButton: 'Akciové ponuky - tlačidlo',
  dealsButtonHref: 'Akciové ponuky - tlačidlo odkaz',
  /** Slug of the category whose products fill the section (VÝPREDAJ). */
  dealsCategory: 'Akciové ponuky - kategória',
  advisorTitle: 'Poradňa - nadpis',
  advisorText: 'Poradňa - text',
  advisorDowelTab: 'Poradňa - hmoždinka záložka',
  advisorPaintTab: 'Poradňa - farba záložka',
  advisorMaterial: 'Poradňa - materiál',
  advisorLoad: 'Poradňa - záťaž',
  advisorLoadNote: 'Poradňa - záťaž popis',
  advisorObject: 'Poradňa - predmet',
  advisorTip: 'Poradňa - tip',
  advisorResultTitle: 'Poradňa - odporúčanie nadpis',
  advisorDiameter: 'Poradňa - priemer',
  advisorScrew: 'Poradňa - skrutka',
  advisorSuitableFor: 'Poradňa - vhodné pre',
  advisorAllDowels: 'Poradňa - všetky hmoždinky',
  advisorAllDowelsHref: 'Poradňa - všetky hmoždinky odkaz',
  advisorCall: 'Poradňa - poradíme',
  advisorRoom: 'Poradňa - rozmery',
  advisorRoomNote: 'Poradňa - rozmery popis',
  advisorLength: 'Poradňa - dĺžka',
  advisorWidth: 'Poradňa - šírka',
  advisorHeight: 'Poradňa - výška',
  advisorOpenings: 'Poradňa - okná a dvere',
  /** m² deducted per window/door – a number typed in the administration. */
  advisorOpeningArea: 'Poradňa - plocha okna a dverí (m²)',
  advisorCoats: 'Poradňa - vrstvy',
  /** Coat choices, one per line („1 vrstva“, „2 vrstvy“, …): line N = N coats. */
  advisorCoatOptions: 'Poradňa - vrstvy možnosti',
  advisorCeiling: 'Poradňa - strop',
  advisorCeilingToggle: 'Poradňa - strop prepínač',
  /** Template with {plocha}, {vrstvy}, {balenie}. */
  advisorPaintResult: 'Poradňa - výsledok farby',
  advisorArea: 'Poradňa - plocha',
  advisorCoverage: 'Poradňa - výdatnosť',
  advisorAllPaints: 'Poradňa - všetky farby',
  advisorAllPaintsHref: 'Poradňa - všetky farby odkaz',
  storesTitle: 'Predajne - nadpis',
  storesSearch: 'Predajne - vyhľadávanie',
  citiesTitle: 'Mestá - nadpis',
  citiesText: 'Mestá - text',
  citiesDelivery: 'Mestá - doručenie',
  popularSearchesTitle: 'Najčastejšie hľadané - nadpis',
  brandsTitle: 'Značky - nadpis',
  faqTitle: 'FAQ - nadpis',
  faqSearch: 'FAQ - vyhľadávanie',
  faqBoxTitle: 'FAQ - box nadpis',
  faqBoxText: 'FAQ - box text',
  faqHours: 'FAQ - otváracie hodiny',
  seoTitle: 'SEO - nadpis',
  seoText: 'SEO - text',
  blogTitle: 'Blog - nadpis',
  blogButton: 'Blog - tlačidlo',
  blogButtonHref: 'Blog - tlačidlo odkaz',
  blogReadMore: 'Blog - odkaz článku',
  newsletterTitle: 'Newsletter - nadpis',
  newsletterText: 'Newsletter - text',
  newsletterPlaceholder: 'Newsletter - placeholder',
  newsletterButton: 'Newsletter - tlačidlo',
  newsletterConsent: 'Newsletter - súhlas',
  newsletterImage: 'Newsletter - obrázok',
  footerTitle: 'Pätička - nadpis',
  footerHours: 'Pätička - otváracie hodiny',
  footerText: 'Pätička - text',
  footerCopyright: 'Pätička - copyright',
  footerCreatedBy: 'Pätička - vytvoril',
  footerCreatorLogo: 'Pätička - logo tvorcu',
  searchTipLabel: 'Vyhľadávanie - tip štítok',
  searchTipTitle: 'Vyhľadávanie - tip nadpis',
  searchTipText: 'Vyhľadávanie - tip text',
  searchTipButton: 'Vyhľadávanie - tip tlačidlo',
  searchTipHref: 'Vyhľadávanie - tip odkaz',
  searchTipImage: 'Vyhľadávanie - tip obrázok',
  productNotice: 'Detail produktu - upozornenie',
  productDispatch: 'Detail produktu - odoslanie',
} as const;

/** Product detail: where the page finds content that products do not carry themselves. */
export const PRODUCT_DETAIL = {
  /** The notice (`PAGE_FIELDS.productNotice`) is shown on products of these categories (slugs). */
  noticeCategorySlugs: ['ochrana-proti-skodcom'],
  /** Product text field whose `<li>` items are the three highlights next to the title. */
  highlightsField: 'Hlavné vlastnosti',
} as const;

/** Menus (main_type): header = Hlavné menu, top_bar = Topbar, footer = Informácie, Pre zákazníka, Kategórie. */
export const MENU_TYPES = { header: 'header', topbar: 'top_bar', footer: 'footer' } as const;
